'use client';

/**
 Provides for asynchronous initialization of Titan.
 */

import { API_ENV, TITAN_API_KEY } from '@wwe-portal/web/utils/config';
import {
  Settings,
  BrowserTracker,
  webRegistrationWithRetrocompatibility,
  BuildType,
} from '@scopely/web-sdk-analytics';

import { addDDAction } from './datadog';
import { STORE_ITEM_ANALYTICS_ID } from '@wwe-portal/web/utils/constants/keys';
import { getStorageItem } from '../bridge/local-storage';
import { wait } from '@wwe-portal/ui/lib/timer';

const initTracker = () => {
  const getAnalyticsId = () => {
    return getStorageItem(STORE_ITEM_ANALYTICS_ID) || '';
  };

  if (!TITAN_API_KEY) throw new Error('TITAN_API_KEY is missing');

  const debug = API_ENV !== 'prod';
  const buildType = API_ENV === 'prod' ? BuildType.Production : BuildType.Debug;
  const config = new Settings(TITAN_API_KEY, buildType)
    .withDebug(debug)
    .withFlushIntervalMillis(5000);

  const retrocompatibleRegistration = webRegistrationWithRetrocompatibility(
    config,
    getAnalyticsId()
  );
  const tracker = new BrowserTracker(config);

  const regiserTracker = () => {
    return tracker.register(retrocompatibleRegistration);
  };
  regiserTracker();

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const Titan: any = {};

  Titan.SendEvent = function (event: {
    name: string;
    parameters: Record<string, unknown>;
  }) {
    if (event.name && event.parameters) {
      tracker.track(event.name, event.parameters);
      addDDAction('titan-events-count', {
        event: event.name,
        params: event.parameters,
      });
    } else {
      console.error('Trying to send malformed event: ' + JSON.stringify(event));
    }
  };

  return { Titan, tracker, regiserTracker };
};

const ref: { current: ReturnType<typeof initTracker> | null } = {
  current: null,
};

export const getTrackerInstance = () => {
  if (ref.current == null) {
    ref.current = initTracker();
  }
  return ref.current;
};

export const tryGetDeviceToken = async (
  diagnostics?: Record<string, unknown>
) => {
  const { tracker, regiserTracker } = getTrackerInstance();
  let deviceToken: string | undefined = undefined;
  let maxAttempts = 10;
  let attempt = 0;
  const iterations: Array<{
    attempt: number;
    trackerAvailable: boolean;
    tokenFound: boolean;
  }> = [];
  do {
    attempt++;
    const registered = await regiserTracker();
    deviceToken = tracker.getDeviceToken();
    if (diagnostics) {
      iterations.push({
        attempt,
        trackerAvailable: !!registered,
        tokenFound: !!deviceToken,
      });
    }
    if (deviceToken) {
      break;
    }
    maxAttempts--;
    await wait(1000);
  } while (maxAttempts > 0);
  if (diagnostics) {
    diagnostics.totalAttempts = attempt;
    diagnostics.tokenReturned = !!deviceToken;
    diagnostics.iterations = iterations;
  }
  return deviceToken;
};
